import {
  VehicleFuelType,
  VehicleStatus,
} from "@1games-ru/region-shared/dist/enums/vehicle/vehicle";
import { VehicleRelatedDto } from "@1games-ru/region-shared/dist/types/vehicle/vehicle-b";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import VehicleCdn from "@/core/cdn/vehicle/vehicle-cdn";
import { cn } from "@/core/helpers/cn";
import { ElectricIcon, FuelIcon } from "@/shared/icons";
import { VehicleStand } from "@/shared/ui";

function FuelTypeLabel({ fuelType }: { fuelType: VehicleFuelType }) {
  const Icon = fuelType === VehicleFuelType.Electric ? ElectricIcon : FuelIcon;
  return (
    <div className="flex gap-1.5 items-center font-bold self-end">
      <Icon size={16} /> {fuelType}
    </div>
  );
}

type ButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "name">;

type PropsShort = {
  full?: false;
  isActive?: boolean;
  children?: ReactNode | ReactNode[];
} & Pick<VehicleRelatedDto, "model" | "name" | "status"> &
  ButtonProps;

type PropsFull = {
  full: true;
  fuelType?: VehicleFuelType;
  isActive?: boolean;
  children?: ReactNode | ReactNode[];
} & Pick<VehicleRelatedDto, "model" | "name" | "status" | "plateNumber"> &
  ButtonProps;

type Props = PropsFull | PropsShort;

export function VehicleCardButtonDepricated(props: PropsShort): JSX.Element;
export function VehicleCardButtonDepricated(props: PropsFull): JSX.Element;
export default function VehicleCardButtonDepricated(props: Props) {
  const { t } = useTranslation("vehicle");

  // eslint-disable-next-line react/destructuring-assignment
  if (!props.full) {
    const { children, model, name, status, isActive, className, ...rest } = props as PropsShort;
    return (
      <button
        type="button"
        className={cn(
          "group relative",
          "w-[10rem] h-[12.5rem]",
          "flex flex-col items-center gap-2.5 p-2.5 bg-dark-800 rounded-[0.75rem]",

          "after:content-[''] after:absolute after:inset-0 after:rounded-2xl",
          "after:bg-gradient-to-br after:from-accent-solid/30 after:to-transparent",
          "after:opacity-0 after:transition-all after:duration-200 after:ease-in-out",
          "hover:after:opacity-100 active:after:brightness-90",
          "after:z-[1]",
          {
            "after:opacity-100": isActive,
          },
          className,
        )}
        {...rest}
      >
        <div className="relative z-[2]">
          <img
            src={VehicleCdn.getVehicleImage(model)}
            // src="/img/vehicle/toros.png"
            className={clsx(
              "w-[11.75rem] aspect-video",
              status !== VehicleStatus.Pending ? "brightness-[30%]" : "",
            )}
            alt={name.brandName}
          />
        </div>

        <div className="flex flex-col h-full w-full overflow-clip relative z-[2] mt-auto">
          <div className="text-white text-15 font-medium text-left">{name.brandName}</div>
          <div className="text-white/50 text-15 font-bold text-left mt-0.5 group-hover:text-accent-solid transition-all duration-200 ease-in-out">
            {name.modelName}
          </div>
        </div>

        {children}
      </button>
    );
  }

  const {
    full,
    children,
    model,
    name,
    fuelType,
    status,
    plateNumber,
    isActive,
    className,
    ...rest
  } = props as PropsFull;

  return (
    <button
      type="button"
      className={clsx(
        "relative flex flex-col justify-end",
        "w-[15.25rem] h-[17.5rem]",
        "rounded-[0.625rem] overflow-clip bg-dark-800 p-5",

        "after:content-[''] after:absolute after:inset-0 after:rounded-2xl",
        "after:bg-gradient-to-br after:from-accent-solid/30 after:to-transparent",
        "after:opacity-0 after:transition-all after:duration-200 after:ease-in-out",
        "hover:after:opacity-100 active:after:brightness-90",
        "after:z-[1]",
        {
          "after:opacity-100": isActive,
        },
        className,
      )}
      {...rest}
    >
      <div className="z-[2] absolute top-6 left-5 flex flex-col items-start">
        <div className="text-15 font-medium">{name.brandName}</div>
        <div className="text-15 text-white/50 font-medium mt-1">{name.modelName}</div>
      </div>
      <img
        src={VehicleCdn.getVehicleBrandImage(name.brand)}
        className={clsx(
          "z-[2] absolute top-6 right-5 h-[2.625rem]",
          status !== VehicleStatus.Pending ? "brightness-[30%]" : "",
        )}
        alt={name.brandName}
      />
      <div className="z-[2] absolute top-14 -right-10">
        <div
          className="relative w-[16.625rem] aspect-video"
          style={
            {
              "--brand-color": "var(--accent-solid)",
            } as unknown as React.CSSProperties
          }
        >
          <img
            src={VehicleCdn.getVehicleImage(model)}
            // src="/img/vehicle/toros.png"
            alt=""
            className="absolute w-full h-full z-1"
          />
          <VehicleStand className="absolute bottom-0" />
        </div>
      </div>
      {children}

      {(fuelType || plateNumber) && (
        <div className="z-[2] flex justify-between flex-end text-[15px] w-full">
          {fuelType ? (
            <div className="flex gap-1.5 items-center font-bold leading-none">
              <FuelTypeLabel fuelType={fuelType} />
            </div>
          ) : (
            <span />
          )}

          {plateNumber && (
            <div className="flex flex-col leading-none">
              <div className="font-bold text-accent-muted text-right">{plateNumber}</div>
              <div className="font-semibold text-[#ffffff80] text-right">
                {t("vehicleInfo.number")}
              </div>
            </div>
          )}
        </div>
      )}
    </button>
  );
}
